import {
  Box,
  Divider,
  Fab,
  Grid,
  makeStyles,
  Menu,
  MenuItem,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from '@material-ui/core'
import { Add, Inbox } from '@material-ui/icons'
import React, { cloneElement, useContext } from 'react'
import {
  DateField,
  Filter,
  Pagination,
  ReferenceField,
  ResourceComponentProps,
  TextField,
  TextInput,
  useTranslate,
  SelectInput,
  FunctionField,
  Datagrid,
  TopToolbar,
  sanitizeListRestProps,
  useListContext,
  FilterContext,
  FilterButton,
  CreateButton,
  downloadCSV,
  ExportButton,
} from 'react-admin'
import classNames from 'classnames'
import { Link } from 'react-router-dom'
import jsonexport from 'jsonexport'
import { Authority } from '../../core/auth/Authority'
import { TicketDto } from '../../core/dto/ticket.dto'
import { BookingType } from '../../core/enum/BookingType'
import { ResourceName } from '../../core/ResourceName'
import List from '../common/customized-ra-components/List'
import FilteredReferenceInput from '../common/FilteredReferenceInput'
import FilterProps from '../common/FilterProps'
import { MaskField } from '../common/MaskField'
import QuickFilter from '../common/QuickFilter'
import { useHasAuthority } from '../hooks/useHasAuthority'
import { TicketImportButton } from '../ticket-imports/TicketImportButton'
import { VariantDto } from '../../core/dto/variant.dto'
import { CheckExternalParkingTicketButton } from './tickets-list/CheckExternalParkingTicketButton'
import Button from '../common/customized-mui-components/Button'
import { TicketType } from '../../core/enum/TicketType'
import { TextFieldWithMaxChars } from '../common/TextFieldWithMaxChars'
import { SeatDto } from '../../core/dto/seat/seat.dto'

const useStyles = makeStyles((theme: Theme) => ({
  inactiveTicketCode: {
    color: theme.palette.error.main,
    textDecoration: 'line-through',
    textDecorationColor: theme.palette.error.main,
  },
  emptyMessage: {
    textAlign: 'center',
    opacity: theme.palette.type === 'light' ? 0.5 : 0.8,
    margin: '0 1em',
    color:
      theme.palette.type === 'light' ? 'inherit' : theme.palette.text.primary,
  },
  emptyIcon: {
    width: '9em',
    height: '9em',
  },
  emptyToolbar: {
    marginTop: '2em',
    display: 'flex',
    gap: '1em',
    justifyContent: 'center',
    alignItems: 'center',
  },
  buttonToMenuItem: {
    padding: '0 !important',
    margin: '0 !important',
    minWidth: '120px',
    textTransform: 'none',
    font: 'inherit !important',
    background: 'none !important',
    boxShadow: 'none !important',
    color: 'inherit !important',
    justifyContent: 'left',
  },
  floating: {
    color: theme.palette.getContrastText(theme.palette.primary.main),
    backgroundColor: theme.palette.primary.main,
    margin: 0,
    top: 'auto',
    right: 20,
    bottom: 60,
    left: 'auto',
    position: 'fixed',
    zIndex: 1000,
  },
  floatingHigher: {
    bottom: 128,
  },
}))

const TicketExpand = () => {
  const t = useTranslate()
  return (
    <Box padding={1}>
      <Grid container spacing={1}>
        <Grid item xs={4}>
          {t('resources.tickets.fields.bookingId')}
        </Grid>
        <Grid item xs={8} style={{ textAlign: 'center' }}>
          <TextField source="bookingId" />
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid container spacing={1}>
        <Grid item xs={4}>
          {t('resources.tickets.fields.variantId')}
        </Grid>
        <Grid item xs={8} style={{ textAlign: 'center' }}>
          <ReferenceField
            source="bookingId"
            label="resources.tickets.fields.variantId"
            reference={ResourceName.BOOKINGS}
            link={false}
            sortable={false}
          >
            <ReferenceField
              source="variantId"
              reference={ResourceName.VARIANTS}
              link={false}
              sortable={false}
            >
              <FunctionField<VariantDto>
                render={(variant?: VariantDto) =>
                  `${variant?.id}. ${variant?.name}`
                }
              />
            </ReferenceField>
          </ReferenceField>
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <Divider />
      </Grid>

      <Grid container spacing={1}>
        <Grid item xs={4}>
          {t('resources.tickets.fields.event')}
        </Grid>
        <Grid item xs={8} style={{ textAlign: 'center' }}>
          <ReferenceField
            source="bookingId"
            label="resources.tickets.fields.event"
            reference={ResourceName.BOOKINGS}
            link={false}
            sortBy="eventName"
          >
            <ReferenceField
              source="eventId"
              reference={ResourceName.EVENTS}
              link={false}
            >
              <>
                <TextField source="name" /> <DateField source="dateStart" /> (
                <TextField source="id" />)
              </>
            </ReferenceField>
          </ReferenceField>
        </Grid>
      </Grid>
    </Box>
  )
}

const TicketFilters = (props: FilterProps) => {
  const hasAuthority = useHasAuthority()
  const theme = useTheme()
  const translate = useTranslate()
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'))

  return (
    <Filter {...props}>
      <TextInput source="ticketCode" alwaysOn autoFocus />
      {hasAuthority(Authority.VIEW_EVENTS) && (
        <FilteredReferenceInput
          label="resources.tickets.fields.event"
          source="eventId"
          reference={ResourceName.EVENTS}
          sort={{ field: 'dateStart', order: 'ASC' }}
          perPage={smallScreen ? 5 : 15}
          filterSource="search"
          selectWithPaginationInputProps={{
            optionText: 'name',
            showFilter: true,
          }}
        />
      )}
      <TextInput source="bookingId" key="bookingId" />
      <SelectInput
        source="bookingType"
        choices={Object.entries(BookingType).map(([value]) => ({
          id: value,
          name: `resources.enums.bookingType.${value}`,
        }))}
      />
      <SelectInput
        source="ticketType"
        choices={Object.entries(TicketType).map(([value]) => ({
          id: value,
          name: `resources.enums.ticketType.${value}`,
        }))}
      />
      {hasAuthority(Authority.VIEW_OBJECTS) && (
        <FilteredReferenceInput
          source="objectId"
          reference={ResourceName.OBJECTS}
          sort={{ field: 'name', order: 'ASC' }}
          perPage={smallScreen ? 5 : 15}
          filterSource="search"
          selectWithPaginationInputProps={{
            optionText: 'name',
            showFilter: true,
          }}
        />
      )}
      {hasAuthority(Authority.VIEW_VARIANTS) && (
        <FilteredReferenceInput
          source="variantId"
          reference={ResourceName.VARIANTS}
          sort={{ field: 'name', order: 'ASC' }}
          perPage={smallScreen ? 5 : 15}
          filterSource="search"
          selectWithPaginationInputProps={{
            optionText: 'name',
            showFilter: true,
          }}
        />
      )}
      {hasAuthority(Authority.VIEW_SEATS) && (
        <FilteredReferenceInput
          source="seatId"
          reference={ResourceName.SEATS}
          sort={{ field: 'id', order: 'ASC' }}
          perPage={smallScreen ? 5 : 15}
          filterSource="search"
          selectWithPaginationInputProps={{
            optionText: (record: SeatDto) =>
              `${translate('resources.seats.fields.rowAlias')}: ${
                record.rowAlias
              } ${translate('resources.seats.fields.numberAlias')}: ${
                record.numberAlias
              }`,
            showFilter: true,
          }}
        />
      )}
      {hasAuthority(Authority.VIEW_BLOCKS) && (
        <FilteredReferenceInput
          source="blockId"
          reference={ResourceName.BLOCKS}
          sort={{ field: 'id', order: 'ASC' }}
          perPage={smallScreen ? 5 : 15}
          filterSource="search"
          selectWithPaginationInputProps={{
            optionText: 'name',
            showFilter: true,
          }}
        />
      )}
      <QuickFilter source="isBookingBlocked" defaultValue />
      <QuickFilter source="isBookingBanned" defaultValue />
      <QuickFilter source="isBookingWithoutBan" defaultValue />
      <TextInput source="customerName" />
    </Filter>
  )
}

const TicketListActions = ({ ...props }) => {
  const { exporter, filters: filtersProp, ...restProps } = props
  const theme = useTheme()
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const [anchorEl, setAnchorEl] = React.useState(null)
  const resource = ResourceName.TICKETS
  const hasAuthority = useHasAuthority()
  const classes = useStyles()

  const { currentSort, displayedFilters, filterValues, showFilter, total } =
    useListContext(props)
  const filters = useContext(FilterContext) || filtersProp

  const handleCreateClick = (event: any) => {
    setAnchorEl(event.currentTarget)
  }

  const handleCreateClose = () => {
    setAnchorEl(null)
  }

  return (
    <TopToolbar {...sanitizeListRestProps(restProps)}>
      {filtersProp
        ? cloneElement(filtersProp, {
            resource,
            showFilter,
            displayedFilters,
            filterValues,
            context: 'button',
          })
        : filters && <FilterButton />}
      {hasAuthority(Authority.CREATE_TICKETS) &&
        hasAuthority(Authority.CREATE_BOOKINGS) &&
        (!smallScreen ? (
          <Button
            label="const.creation"
            variant="text"
            onClick={handleCreateClick}
          >
            <Add />
          </Button>
        ) : (
          <Fab className={classes.floating} onClick={handleCreateClick}>
            <Add />
          </Fab>
        ))}
      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleCreateClose}
        onClick={handleCreateClose}
      >
        <MenuItem>
          <Button
            component={Link}
            to={`/${ResourceName.BOOKINGS}/create`}
            className={classes.buttonToMenuItem}
            label="ra.action.create"
            variant="text"
            useSmallVersionBreakpoint={false}
          />
        </MenuItem>
        <MenuItem>
          <Button
            component={Link}
            to={`/${ResourceName.TICKETS}/import`}
            className={classes.buttonToMenuItem}
            label="const.import"
            variant="text"
            useSmallVersionBreakpoint={false}
          />
        </MenuItem>
        {hasAuthority(Authority.VIEW_TICKET_IMPORT_TEMPLATES) && (
          <MenuItem>
            <TicketImportButton
              buttonProps={{
                label: 'resources.ticket-imports.import20',
                variant: 'text',
                className: classes.buttonToMenuItem,
              }}
            />
          </MenuItem>
        )}
      </Menu>
      {!hasAuthority(Authority.CREATE_TICKETS) &&
        hasAuthority(Authority.CREATE_BOOKINGS) && (
          <CreateButton
            resource={ResourceName.BOOKINGS}
            basePath={`/${ResourceName.BOOKINGS}`}
          />
        )}
      {hasAuthority(Authority.EXTERNAL_PARKING_GET_TICKET_BUTTON) && (
        <CheckExternalParkingTicketButton
          variant="text"
          size="small"
          className={classNames(classes.floating, classes.floatingHigher)}
        />
      )}
      {exporter !== false && (
        <ExportButton
          disabled={total === 0 || total >= 150000}
          resource={resource}
          sort={currentSort}
          filterValues={filterValues}
          maxResults={150000}
        />
      )}
    </TopToolbar>
  )
}

const TicketEmpty = () => {
  const classes = useStyles()
  const translate = useTranslate()

  return (
    <>
      <div className={classes.emptyMessage}>
        <Inbox className={classes.emptyIcon} />
        <Typography variant="h4" paragraph>
          {translate('resources.tickets.empty')}
        </Typography>
        <Typography variant="body1">
          {translate('resources.tickets.invite')}
        </Typography>
      </div>
      <div className={classes.emptyToolbar}>
        <CreateButton
          resource={ResourceName.BOOKINGS}
          basePath={`/${ResourceName.BOOKINGS}`}
          variant="contained"
        />
        <CreateButton
          resource={ResourceName.TICKETS}
          label="const.import"
          color="primary"
          size="small"
          variant="contained"
        />
      </div>
    </>
  )
}

const TicketListExport = (tickets: TicketDto[]) => {
  jsonexport(tickets, (err, csv) => {
    downloadCSV(csv, 'tickets')
  })
}

export const TicketList = (props: ResourceComponentProps) => {
  const classes = useStyles()
  const theme = useTheme()
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const xSmallScreen = useMediaQuery(theme.breakpoints.down('xs'))
  const hasAuthority = useHasAuthority()

  return (
    <List
      {...props}
      exporter={TicketListExport}
      perPage={20}
      sort={{ field: 'id', order: 'DESC' }}
      pagination={<Pagination rowsPerPageOptions={[10, 20, 50, 100, 200]} />}
      filters={<TicketFilters />}
      bulkActionButtons={false}
      actions={<TicketListActions />}
      empty={<TicketEmpty />}
    >
      <Datagrid
        padding={smallScreen ? 'checkbox' : 'default'}
        rowClick={
          hasAuthority(Authority.VIEW_BOOKINGS)
            ? (id, basePath, record) =>
                `${ResourceName.BOOKINGS}/${record?.bookingId}/show`
            : undefined
        }
        isRowExpandable={() => xSmallScreen}
        expand={<TicketExpand />}
        hasBulkActions={false}
      >
        {!smallScreen &&
          hasAuthority(Authority.VIEW_BOOKINGS) &&
          hasAuthority(Authority.VIEW_CUSTOMERS) && (
            <ReferenceField
              source="bookingId"
              label="resources.tickets.fields.firstname"
              reference={ResourceName.BOOKINGS}
              link={false}
              sortBy="customerFirstname"
            >
              <ReferenceField
                source="customerId"
                reference={ResourceName.CUSTOMERS}
                link={false}
              >
                <TextField source="firstname" />
              </ReferenceField>
            </ReferenceField>
          )}
        {!smallScreen &&
          hasAuthority(Authority.VIEW_BOOKINGS) &&
          hasAuthority(Authority.VIEW_CUSTOMERS) && (
            <ReferenceField
              source="bookingId"
              label="resources.tickets.fields.lastname"
              reference={ResourceName.BOOKINGS}
              link={false}
              sortBy="customerLastname"
            >
              <ReferenceField
                source="customerId"
                reference={ResourceName.CUSTOMERS}
                link={false}
              >
                <TextField source="lastname" />
              </ReferenceField>
            </ReferenceField>
          )}
        {!smallScreen &&
          hasAuthority(Authority.VIEW_BOOKINGS) &&
          hasAuthority(Authority.VIEW_CUSTOMERS) && (
            <ReferenceField
              source="bookingId"
              label="resources.tickets.fields.pesel"
              reference={ResourceName.BOOKINGS}
              link={false}
              sortBy="customerPesel"
            >
              <ReferenceField
                source="customerId"
                reference={ResourceName.CUSTOMERS}
                link={false}
              >
                <MaskField source="pesel" />
              </ReferenceField>
            </ReferenceField>
          )}
        {smallScreen &&
          hasAuthority(Authority.VIEW_BOOKINGS) &&
          hasAuthority(Authority.VIEW_CUSTOMERS) && (
            <ReferenceField
              source="bookingId"
              label="resources.tickets.fields.customer"
              reference={ResourceName.BOOKINGS}
              link={false}
              sortBy="customerPesel"
            >
              <ReferenceField
                source="customerId"
                reference={ResourceName.CUSTOMERS}
                link={false}
              >
                <>
                  <TextField source="firstname" />{' '}
                  <TextField source="lastname" /> <MaskField source="pesel" />
                </>
              </ReferenceField>
            </ReferenceField>
          )}
        <FunctionField<TicketDto>
          source="ticketCode"
          render={(record?: TicketDto) => (
            <TextFieldWithMaxChars
              source="ticketCode"
              maxChars={smallScreen ? 10 : 64}
              className={
                record?.active === false
                  ? classes.inactiveTicketCode
                  : undefined
              }
            />
          )}
        />
        {!smallScreen &&
          hasAuthority(Authority.VIEW_BOOKINGS) &&
          hasAuthority(Authority.VIEW_EVENTS) && (
            <ReferenceField
              source="bookingId"
              label="resources.tickets.fields.event"
              reference={ResourceName.BOOKINGS}
              link={false}
              sortBy="eventName"
            >
              <ReferenceField
                source="eventId"
                reference={ResourceName.EVENTS}
                link={false}
              >
                <>
                  <TextField source="name" /> <DateField source="dateStart" /> (
                  <TextField source="id" />)
                </>
              </ReferenceField>
            </ReferenceField>
          )}
        {!smallScreen && hasAuthority(Authority.VIEW_VARIANTS) && (
          <ReferenceField
            source="bookingId"
            label="resources.tickets.fields.variantId"
            reference={ResourceName.BOOKINGS}
            link={false}
            sortable={false}
          >
            <ReferenceField
              source="variantId"
              reference={ResourceName.VARIANTS}
              link={false}
              sortable={false}
            >
              <FunctionField<VariantDto>
                render={(variant?: VariantDto) =>
                  `${variant?.id}. ${variant?.name}`
                }
              />
            </ReferenceField>
          </ReferenceField>
        )}
        {!xSmallScreen && <TextField source="bookingId" />}
      </Datagrid>
    </List>
  )
}
